import { EditorModes, WacVersion } from "client/Api/Documents/common";
import {
	ReactElement,
	RefObject,
	SyntheticEvent,
	useCallback,
	useRef,
	useState
} from "react";
import styles from "./DocumentWindow.module.less";
import classNames from "classnames";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	IFrame,
	IFramePermission,
	Window
} from "client/Components";
import { deviceHelper } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { createRoot, Root } from "react-dom/client";

export type DocumentWindowProps = {
	file: { name: string; };
	url: string;
	version: WacVersion;
	shouldHideRibbon?: boolean;
	editorMode: EditorModes;
	onClose: () => void;
	isResizable: boolean;
};

export const DocumentWindow = (props: DocumentWindowProps): ReactElement => {
	const {
		file,
		url,
		isResizable,
		version,
		shouldHideRibbon = false,
		editorMode,
		onClose
	} = props;

	const iframeRef: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);
	const isUnloadedRef: RefObject<boolean> = useRef<boolean>(false);
	const [isFreezeDialogVisible, setIsFreezeDialogVisible] = useState<boolean>(false);

	const unload = useCallback(
		(): void => {
			const shouldShowFreezeDialog: boolean = deviceHelper.wopi
				.freezeDialog
				.shouldShow();

			if (shouldShowFreezeDialog) {
				setIsFreezeDialogVisible(true);
			}

			const iframe: HTMLIFrameElement = ensureNotNil(iframeRef.current);

			// The functionality to unload the iframe is wrapped in an setTimeout, because slower devices
			// (like the Snappet 10" tablet) will not show the freezeDialog otherwise
			setTimeout(
				(): void => {
					iframe.src = URL_TO_FORCE_UNLOAD;

					if (shouldShowFreezeDialog) {
						setTimeout(
							(): void => {
								// Always close the freeze dialog after the regular "event flow" continues
								// (i.e. after the onbeforeunload event handler is completed)
								setIsFreezeDialogVisible(false);
							},
							1
						);
					}
				},
				1
			);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			// When the iframe has been successfully unloaded, the "real" close of the document window has to take place
			if (isUnloadedRef.current) {
				onClose();
				return;
			}

			unload();
		},
		[onClose, unload]
	);

	const loadHandler = useCallback(
		(args: SyntheticEvent<HTMLIFrameElement>): void => {
			// In the past we were able to reliably determine whether a user clicked
			// "Stay on the page" cross browser, however since February 2018 Chrome's implementation
			// of firing events changed resulting that we would always see that a user clicked
			// "Stay on the page" even when it was not the case.
			//
			// We can't access the actual location of the window in the iframe due to
			// cross domain restrictions so we rely on the src attribute on the iframe.
			// However this can be very inaccurate as the user can stop the navigation
			// invoked through settings the src attribute by clicking "Stay on the page"
			// in an onbeforeunload event handler (this is mostly the case when there is
			// no auto save and the user needs to manual save the changed (WAC 2013)).
			//
			// Known limitation to this solution:
			// - User clicks close
			// - User clicks "Stay on the page"
			// - User invokes an action resulting in the page to be refreshed / load a different page
			// - The editor will close
			//
			// One way to reproduce this in WAC 2013 by switching to "Read" view as the action to invoke the
			// page refresh
			const urlOnIframe: string = args.currentTarget
				.src;

			if (urlOnIframe !== URL_TO_FORCE_UNLOAD) {
				return;
			}

			if (isUnloadedRef.current) {
				return;
			}

			isUnloadedRef.current = true;

			closeHandler();
		},
		[closeHandler]
	);

	// When the ribbon is hidden we don't have any overlays at all
	const shouldRenderFileMenuOverlay: boolean = !shouldHideRibbon
		// We only have overlays in edit mode
		&& editorMode === EditorModes.Edit
		// The file overlay is not present for OnPremises as it is required for saving
		&& version !== WacVersion.OnPremises;

	return (
		<Window
			trackingId={generateTrackingId("document-window", `|${file.name}`)}
			title={file.name}
			isResizable={isResizable}
			onClose={closeHandler}
		>
			<div
				className={classNames(
					styles.root,
					modeStyleMap[editorMode],
					versionStyleMap[version],
					{ [styles.root_hideRibbon]: shouldHideRibbon }
				)}
			>

				{shouldRenderFileMenuOverlay && (
					<div
						className={classNames(
							styles.fileMenuOverlay,
							getCurrentCulture()
						)}
					/>
				)}

				<IFrame
					permissions={documentWindowIframePermissions}
					trackingId="iframe"
					className={styles.iframe}
					onLoad={loadHandler}
					src={url}
					ref={iframeRef}
				/>

				{isFreezeDialogVisible && (
					<Dialog trackingId="document-window-freeze-dialog" isEmbedded>
						<Dialog.Header title={localize("_PleaseWait")} />
						<Dialog.Body>
							{localize("_OfficeAppFrezingNotificationMessage")}
						</Dialog.Body>
					</Dialog>
				)}
			</div>
		</Window>
	);
};
DocumentWindow.displayName = "DocumentWindow";

const URL_TO_FORCE_UNLOAD: string = "about:blank";

const modeStyleMap: Record<EditorModes, string | undefined> = {
	[EditorModes.View]: undefined,
	[EditorModes.Edit]: undefined,
	[EditorModes.MobileView]: undefined,
	[EditorModes.InteractivePreview]: styles.root_interactivePreview
};

const versionStyleMap: Record<WacVersion, string> = {
	[WacVersion.Office365]: styles.root_wacOnline,
	[WacVersion.OnPremises]: styles.root_wacHosted
};

const documentWindowIframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];

export function openDocumentWindow(
	props: Omit<DocumentWindowProps, "onClose"> & { onClose?: () => void; }
): void {
	let host: HTMLDivElement | null = document.createElement("div");
	let root: Root | null = createRoot(host);
	host.style.display = "none";
	document.body.append(host);

	const destroy = (): void => {
		root?.unmount();
		root = null;

		host?.remove();
		host = null;

		props.onClose?.();
	};

	root.render(
		<DocumentWindow
			{...props}
			onClose={destroy}
		/>
	);
}
